import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, EnterpriseFormModal, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import type { FormModalMode } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { taxClassificationService, taxMetaService } from 'api/services';
import type {
  TaxClassificationDto, TaxClassificationQueryParams, TaxJurisdictionDto, TaxUnitDto, PaginationMeta,
} from 'api/types';

// ============================================================================
// TAX CLASSIFICATIONS — the tenant-authored classification master of the entity's
// jurisdiction (ADR-0047 Phase 2 §7): HSN / SAC under India GST, or whatever code a regime
// reports items under. Lightweight by design — nothing seeds the exhaustive statutory list;
// a tenant adds the handful of codes it sells under. The Service Master's HSN / SAC picker
// searches it, the HSN-wise summary prints its descriptions, and Tax Settings → Defaults
// decides whether an item may carry a code that is not here (enforcement).
// ============================================================================

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];
const CODE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'SERVICES', label: 'Services (SAC)' },
  { value: 'GOODS', label: 'Goods (HSN)' },
];

interface ClassificationForm {
  code: string;
  description: string;
  codeType: string;
  defaultUnitCode: string;
  effectiveFrom: string;
  effectiveTo: string;
  statusCode: string;
}
const EMPTY_FORM: ClassificationForm = {
  code: '', description: '', codeType: 'SERVICES', defaultUnitCode: '', effectiveFrom: '', effectiveTo: '', statusCode: 'ACTIVE',
};

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];
const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'code', label: 'Code' },
  { id: 'description', label: 'Description' },
  { id: 'codeType', label: 'Type' },
  { id: 'defaultUnitCode', label: 'Default Unit' },
  { id: 'effective', label: 'In force' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

const fmtDate = (d?: string | null) => (d ? d.slice(0, 10) : '');

interface TaxClassificationsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const TaxClassificationsScreen: React.FC<TaxClassificationsScreenProps> = ({ moduleIcon, tabId, moduleName = 'Tax Classifications' }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('tax-classifications');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [jurisdiction, setJurisdiction] = useState<TaxJurisdictionDto | null | undefined>(undefined);
  const [units, setUnits] = useState<TaxUnitDto[]>([]);
  // A classification master exists only where the jurisdiction DECLARES a classification field
  // on its items (SERVICE schema, option source TAX.CLASSIFICATION — HSN / SAC under India
  // GST). Botswana VAT declares none, so the screen explains that instead of offering a form;
  // the server refuses a create for such a jurisdiction too.
  const [declares, setDeclares] = useState<boolean>(true);
  useEffect(() => {
    (async () => {
      const j = await taxMetaService.getEntityJurisdiction().catch(() => null);
      setJurisdiction(j);
      if (j) {
        const ctx = await taxMetaService.getFieldContext('SERVICE').catch(() => null);
        setDeclares(!!ctx?.fields?.some(f => (f.optionSource || '').toUpperCase() === 'TAX.CLASSIFICATION'));
        setUnits(await taxClassificationService.getUnits().catch(() => []));
      }
    })();
  }, []);

  // List
  const [queryParams, setQueryParams] = useState<TaxClassificationQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<TaxClassificationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);
  const refetch = useCallback(async () => {
    if (!jurisdiction) { setData([]); setPagination(DEFAULT_PAGINATION); return; }
    setLoading(true);
    try {
      const res = await taxClassificationService.getAll(queryParams);
      setData(res.data); setPagination(res.pagination);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the classifications.');
    } finally { setLoading(false); }
  }, [jurisdiction, queryParams]);
  useEffect(() => { void refetch(); }, [refetch]);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('tax-classifications-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeTypeFilter, setCodeTypeFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Form
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormModalMode>('create');
  const [selected, setSelected] = useState<TaxClassificationDto | null>(null);
  const createCounter = useRef(0);

  const unitOptions = useMemo<SelectOption[]>(
    () => units.map(u => ({ value: u.unitCode, label: `${u.unitCode} — ${u.unitName}` })), [units]);

  const handleAdd = useCallback(() => {
    createCounter.current += 1;
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, []);
  const handleEdit = useCallback((r: TaxClassificationDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.code}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);
  const handleCopy = useCallback((r: TaxClassificationDto) => {
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, []);
  const handleDelete = useCallback(async (r: TaxClassificationDto) => {
    const ok = await confirmDelete({ resourceLabel: `${r.code} — ${r.description}` });
    if (!ok) return;
    try {
      await taxClassificationService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Classification "${r.code}" deleted.`);
      void refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete.');
    }
  }, [confirmDelete, refetch]);

  const populateForm = useCallback((mode: FormModalMode, record: TaxClassificationDto | null): ClassificationForm => record
    ? {
        code: mode === 'copy' ? '' : record.code,
        description: record.description,
        codeType: record.codeType || 'SERVICES',
        defaultUnitCode: record.defaultUnitCode || '',
        effectiveFrom: fmtDate(record.effectiveFrom),
        effectiveTo: fmtDate(record.effectiveTo),
        statusCode: record.statusCode || 'ACTIVE',
      }
    : { ...EMPTY_FORM }, []);

  const validateForm = useCallback((form: ClassificationForm): Record<string, string> => {
    const e: Record<string, string> = {};
    const code = form.code.trim();
    if (!code) e.code = 'Code is required.';
    else if (code.length < 2 || code.length > 20 || !/^[A-Za-z0-9]+$/.test(code)) e.code = 'Letters or digits only, 2–20 characters (HSN 4/6/8 digits, SAC 6 digits).';
    if (!form.description.trim()) e.description = 'A description is required — the HSN-wise summary prints it.';
    if (form.effectiveFrom && form.effectiveTo && form.effectiveTo < form.effectiveFrom) e.effectiveTo = 'Effective-to cannot precede effective-from.';
    return e;
  }, []);

  const saveForm = useCallback(async (form: ClassificationForm, mode: FormModalMode, record: TaxClassificationDto | null) => {
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      codeType: form.codeType,
      defaultUnitCode: form.defaultUnitCode || null,
      effectiveFrom: form.effectiveFrom || null,
      effectiveTo: form.effectiveTo || null,
      statusCode: form.statusCode,
    };
    if (mode === 'edit' && record) await taxClassificationService.update(record.id, { ...payload, rowVersion: record.rowVersion });
    else await taxClassificationService.create(payload);
  }, []);

  const columns: SyutGridColumn<TaxClassificationDto>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 130, cell: ({ row }) => <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{(row.original as TaxClassificationDto).code}</span> },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 320 },
    {
      id: 'codeType', header: 'Type', accessorKey: 'codeType', size: 120,
      cell: ({ row }) => <span className="badge">{(row.original as TaxClassificationDto).codeType === 'GOODS' ? 'Goods' : 'Services'}</span>,
    },
    {
      id: 'defaultUnitCode', header: 'Default Unit', accessorKey: 'defaultUnitCode', size: 140,
      cell: ({ row }) => {
        const r = row.original as TaxClassificationDto;
        if (!r.defaultUnitCode) return '—';
        const u = units.find(x => x.unitCode === r.defaultUnitCode);
        return u ? `${u.unitCode} — ${u.unitName}` : r.defaultUnitCode;
      },
    },
    {
      id: 'effective', header: 'In force', size: 180,
      cell: ({ row }) => {
        const r = row.original as TaxClassificationDto;
        if (!r.effectiveFrom && !r.effectiveTo) return <span style={{ color: 'var(--text-muted)' }}>Always</span>;
        return `${fmtDate(r.effectiveFrom) || '…'} → ${fmtDate(r.effectiveTo) || '…'}`;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => <span className="badge">{(row.original as TaxClassificationDto).statusCode === 'INACTIVE' ? 'Inactive' : 'Active'}</span>,
    },
  ], [units]);

  const rowActions = useCallback((r: TaxClassificationDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleEdit, handleCopy, handleDelete]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Classification'), visible: can('CREATE') && !!jurisdiction && declares },
    actionButtonFactory.refresh(() => { void refetch(); }),
  ];

  return (
    <EnterpriseScreenShell
      title="Tax Classifications"
      description={jurisdiction ? `The classification codes (HSN / SAC …) items report under ${jurisdiction.jurisdictionName}` : 'The classification codes items report under'}
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.tag size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code / description...' }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: [codeTypeFilter, includeInactive ? '1' : ''].filter(Boolean).length,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type"
                  options={[{ value: '', label: 'All' }, ...CODE_TYPE_OPTIONS]}
                  value={codeTypeFilter}
                  onChange={v => setCodeTypeFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setCodeTypeFilter(''); setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({ ...p, codeType: codeTypeFilter || undefined, includeInactive: includeInactive || undefined, page: 1 }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {jurisdiction === null && (
          <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            This entity has no tax jurisdiction. Set one under Tax → Tax Settings; classifications belong to a jurisdiction.
          </div>
        )}
        {jurisdiction && !declares && (
          <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
            <strong>{jurisdiction.jurisdictionName}</strong> declares no classification codes for items, so there is nothing to maintain here.
            A classification master (HSN / SAC) exists only under a jurisdiction whose item fields include one — India GST does; Botswana VAT does not.
          </div>
        )}
        {jurisdiction && declares && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'code']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No classifications yet — add the codes your items report under.'} />
        )}
      </div>

      {jurisdiction && declares && (
        <EnterpriseFormModal<ClassificationForm, TaxClassificationDto>
          isOpen={formOpen}
          mode={formMode}
          record={selected}
          noun="Classification"
          size="md"
          emptyForm={EMPTY_FORM}
          populateOnOpen={populateForm}
          validate={validateForm}
          onSave={saveForm}
          confirmSaveMessage={(mode, form) => (mode === 'edit'
            ? `Save the changes to ${form.code.toUpperCase()}? Items mapped to it keep the code; only the description and unit change.`
            : `Add ${form.code.toUpperCase()} to the ${jurisdiction.jurisdictionCode} classification master?`)}
          successMessage={(mode) => (mode === 'edit' ? 'Classification updated.' : 'Classification added.')}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={String(createCounter.current)}
          recordKey={selected?.code}
          getStripFormLabel={(f) => f.code || undefined}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); void refetch(); }}
          onRestoreModal={() => setFormOpen(true)}
        >
          {({ form, setForm, mode, getFieldError, clearFieldError, isSaving }) => {
            const upd = (patch: Partial<ClassificationForm>) => setForm(prev => ({ ...prev, ...patch }));
            const err = (f: string) => getFieldError(f);
            return (
              <>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Code <span className="text-danger">*</span></label>
                    <input className={`form-input${err('code') ? ' is-invalid' : ''}`} value={form.code} maxLength={20} disabled={isSaving}
                      readOnly={mode === 'edit'} style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                      placeholder={form.codeType === 'GOODS' ? 'e.g. 8471' : 'e.g. 998311'}
                      onChange={e => { clearFieldError('code'); upd({ code: e.target.value.toUpperCase() }); }} />
                    {err('code')
                      ? <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{err('code')}</div>
                      : mode === 'edit' && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>The code is what items store; add a new code rather than renaming one in use.</div>}
                  </div>
                  <div className="form-group">
                    <SyutSelect label="Type" options={CODE_TYPE_OPTIONS} value={form.codeType} isDisabled={isSaving}
                      onChange={v => upd({ codeType: (v as string) || 'SERVICES' })} isClearable={false} menuPortalTarget={document.body} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description <span className="text-danger">*</span></label>
                  <input className={`form-input${err('description') ? ' is-invalid' : ''}`} value={form.description} maxLength={200} disabled={isSaving}
                    placeholder="As it should print on the HSN-wise summary"
                    onChange={e => { clearFieldError('description'); upd({ description: e.target.value }); }} />
                  {err('description') && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{err('description')}</div>}
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <SyutSelect label="Default unit" options={unitOptions} value={form.defaultUnitCode} isDisabled={isSaving || unitOptions.length === 0}
                      onChange={v => upd({ defaultUnitCode: (v as string) || '' })} isClearable menuPortalTarget={document.body}
                      placeholder={unitOptions.length === 0 ? 'This jurisdiction reports no units' : 'Suggested when an item picks this code'} />
                  </div>
                  <div className="form-group">
                    <SyutSelect label="Status" options={STATUS_OPTIONS} value={form.statusCode} isDisabled={isSaving}
                      onChange={v => upd({ statusCode: (v as string) || 'ACTIVE' })} isClearable={false} menuPortalTarget={document.body} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Effective from</label>
                    <input type="date" className="form-input" value={form.effectiveFrom} disabled={isSaving} onChange={e => upd({ effectiveFrom: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Effective to</label>
                    <input type="date" className={`form-input${err('effectiveTo') ? ' is-invalid' : ''}`} value={form.effectiveTo} disabled={isSaving}
                      onChange={e => { clearFieldError('effectiveTo'); upd({ effectiveTo: e.target.value }); }} />
                    {err('effectiveTo') && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{err('effectiveTo')}</div>}
                  </div>
                </div>
              </>
            );
          }}
        </EnterpriseFormModal>
      )}
    </EnterpriseScreenShell>
  );
};

export default TaxClassificationsScreen;
